import { ReactNode } from "react";
import { AlertCircle, Database, Zap } from "lucide-react";
import {
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassCardTitle,
} from "@/components/ui/glass-card";
import { Separator } from "@/components/ui/separator";
import { tDeploymentScope } from "@/i18n/deploymentScopeStrings";
import { formatDeploymentScope } from "@/services/wizardSummaryFormat";
import { DeviceCountInfoTooltip } from "@/components/applications/shared/DeviceCountInfoTooltip";

/**
 * Shape passed in for the live device-count behavior we built up across the
 * Deployment scope iterations. Mirrors the {@link useTagsDeviceCount} hook
 * return so callers can forward it directly.
 */
export interface DeviceCountState {
  total: number;
  isLoading: boolean;
  hasError: boolean;
  isExact: boolean;
  /**
   * Optional. When `true`, every tag's count was served from
   * react-query's cache without a network request. The panel surfaces
   * this with a small "Using cached counts" badge so users can tell the
   * number wasn't recomputed against the live API. Defaults to `false`
   * when omitted, preserving the previous (always-show-as-fresh)
   * rendering for callers that haven't been updated yet.
   */
  fromCache?: boolean;
}

export interface DeploymentSummaryPanelProps {
  /**
   * Top header strip — wizard-specific identity (e.g. App name + Bundle ID
   * for App Config; App name + Path for PPPC). Optional. Pass at most a
   * couple of `{label, value, mono}` items.
   */
  identity?: Array<{ label: string; value: string; mono?: boolean }>;
  /** Number of customers to display in the first tile. */
  customerCount: number;
  /** Tag display: count + the names of currently-selected tags. */
  selectedTagNames: string[];
  /** Live device-count state for the Tags tile subtitle. */
  deviceCount: DeviceCountState;
  /** Number to display in the Payloads tile (caller decides what counts). */
  payloadCount: number;
  /** Label override for the Payloads tile (e.g. "Services configured"). */
  payloadLabel?: string;
  /** Number to display in the Platforms tile. */
  platformCount: number;
  /**
   * When true, suppresses the "draft — no tags" alert at the bottom. Use
   * for surfaces that show their own validation banner.
   */
  hideDraftAlert?: boolean;
  /**
   * Whether the host wizard requires explicit tag selection (external
   * targets) or inherits its scope from the parent app (catalog targets).
   *
   * Default `true` preserves the original external-only behavior — every
   * existing call site keeps working without changes.
   *
   * When `false` ("catalog mode"):
   *  - The Tags tile subtitle reads "Follows the app" (via
   *    {@link tDeploymentScope}'s `followsApp` key) regardless of how many
   *    tags are technically selected. Users cannot pick tags for catalog
   *    targets, so showing "Select one or more tags below" would be a lie.
   *  - The "no tags selected → draft" alert is suppressed for the same
   *    reason — catalog targets aren't drafts when their tag list is empty.
   *  - The DeviceCountInfoTooltip still renders, but explains the catalog
   *    inheritance instead of the upper-bound math (the tooltip itself
   *    branches on `selectedTagCount`).
   *  - The 2+ tag upper-bound caveat is suppressed.
   *
   * This knob lets the wizards mount the panel on their last step in
   * catalog mode too — useful for both the user-facing summary continuity
   * and for end-to-end tests that want to assert the tooltip in either
   * mode without contorting the catalog flow.
   */
  tagsRequired?: boolean;
  /** Extra content rendered below the tile grid (errors, alerts, etc.). */
  footer?: ReactNode;
}

/**
 * Unified Deployment Summary panel used by App Config and PPPC wizards.
 *
 * Layout matches `ScopeAndAssignStep`'s built-in summary — 4-up tiles for
 * Customers / Tags / Payloads / Platforms — so the wizards visually align
 * with the shared step. The Tags tile carries the recent UX wins:
 *  - live device-count subtitle (singular = exact, multi = upper bound),
 *  - "Calculating devices…" placeholder while counts load,
 *  - info tooltip explaining single vs multi-tag derivation,
 *  - upper-bound caveat below the tile when 2+ tags are picked.
 *
 * Wizard-specific identity (App name, Bundle ID, Path, …) is rendered as a
 * compact header strip above the tiles so we don't lose context that the
 * shared summary has no concept of.
 */
export const DeploymentSummaryPanel = ({
  identity,
  customerCount,
  selectedTagNames,
  deviceCount,
  payloadCount,
  payloadLabel = "Payloads",
  platformCount,
  hideDraftAlert,
  tagsRequired = true,
  footer,
}: DeploymentSummaryPanelProps) => {
  const tagCount = selectedTagNames.length;
  const isLoadingCount =
    tagsRequired &&
    tagCount > 0 &&
    deviceCount.isLoading &&
    !deviceCount.hasError;
  // External-only: the count tooltip / suffix only makes sense when the
  // user actually picks tags. Catalog targets get a fixed "Follows the app"
  // story below.
  const showCount =
    tagsRequired &&
    tagCount > 0 &&
    !deviceCount.isLoading &&
    !deviceCount.hasError;

  const tagsSubtitle = !tagsRequired
    ? // Catalog mode — scope is inherited from the parent app, regardless
      // of any leftover tag selections from a prior mode flip.
      tDeploymentScope("followsApp")
    : isLoadingCount
      ? tDeploymentScope("calculatingDevices")
      : tagCount === 0
        ? tDeploymentScope("selectTagsPrompt")
        : formatDeploymentScope({
            tagsRequired: true,
            selectedTagNames,
            deviceCount: deviceCount.hasError ? undefined : deviceCount.total,
            exact: deviceCount.isExact,
          });

  return (
    <GlassCard
      variant="strong"
      className="border-primary/20 bg-primary/5 max-w-5xl mx-auto"
      aria-label="Deployment summary"
    >
      <GlassCardHeader>
        <GlassCardTitle className="text-lg flex items-center gap-2">
          <Zap className="h-5 w-5 text-primary" />
          Deployment Summary
        </GlassCardTitle>
      </GlassCardHeader>
      <GlassCardContent>
        {identity && identity.length > 0 && (
          <div
            className="mb-4 pb-4 border-b border-border/40 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2"
            data-testid="deployment-summary-identity"
          >
            {identity.map((row) => (
              <div
                key={row.label}
                className="flex items-baseline justify-between gap-3 min-w-0"
              >
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {row.label}
                </span>
                <span
                  className={`text-sm text-right truncate ${
                    row.mono ? "font-mono" : ""
                  }`}
                  title={row.value || undefined}
                >
                  {row.value || "—"}
                </span>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          <SummaryTile
            label="Customers"
            value={customerCount}
            testId="summary-tile-customers"
          />
          <SummaryTile
            label="Tags assigned"
            value={tagCount}
            testId="summary-tile-tags"
            subtitle={
              <>
                <span
                  className="block text-xs text-muted-foreground mt-1 break-words"
                  data-testid="deployment-scope-value"
                  aria-live="polite"
                  aria-busy={isLoadingCount}
                >
                  {tagsSubtitle}
                </span>
                {/*
                 * Cache-status badge — only meaningful when we're actually
                 * showing a count (skipping when loading, erroring, or
                 * before any tag is picked). The flag is optional on
                 * `DeviceCountState` so existing callers that don't yet
                 * forward `fromCache` simply never render the badge.
                 */}
                {showCount && deviceCount.fromCache && (
                  <span
                    className="inline-flex items-center gap-1 mt-1.5 px-1.5 py-0.5 rounded-sm bg-muted text-[10px] font-medium uppercase tracking-wide text-muted-foreground"
                    data-testid="deployment-scope-cached-badge"
                    title={tDeploymentScope("cachedCountsBadge")}
                  >
                    <Database className="h-2.5 w-2.5" aria-hidden="true" />
                    {tDeploymentScope("cachedCountsBadge")}
                  </span>
                )}
              </>
            }
            trailing={
              tagsRequired ? (
                showCount ? (
                  <DeviceCountInfoTooltip
                    selectedTagCount={tagCount}
                    mode="external"
                  />
                ) : null
              ) : (
                // Catalog mode — always show the tooltip; its body explains
                // the "follows the app" inheritance instead of count math.
                <DeviceCountInfoTooltip
                  selectedTagCount={tagCount}
                  mode="catalog"
                />
              )
            }
          />
          <SummaryTile
            label={payloadLabel}
            value={payloadCount}
            testId="summary-tile-payloads"
          />
          <SummaryTile
            label="Platforms"
            value={platformCount}
            testId="summary-tile-platforms"
          />
        </div>

        {tagsRequired && tagCount >= 2 && !deviceCount.isLoading && (
          <>
            {/*
             * DOM separator between the "up to N devices" subtitle inside
             * the Tags tile and the upper-bound caveat below. Without this,
             * `textContent`-based assertions (snapshot/resilient tests, a11y
             * tooling, copy-to-clipboard) concatenate the two strings into
             * "...up to 17 devicesUpper bound: we sum...". The visible
             * <Separator> alone isn't enough — it renders as an empty
             * <div>, which doesn't introduce any whitespace into
             * textContent. The aria-hidden " · " sentinel guarantees a
             * whitespace boundary in the serialized text while staying
             * invisible to assistive tech (the visual divider does the
             * separating job for sighted users).
             */}
            <span
              aria-hidden="true"
              className="sr-only"
              data-testid="deployment-scope-hint-separator"
            >
              {" \u00b7 "}
            </span>
            <Separator className="mt-3" />
            <p
              className="text-xs text-muted-foreground text-center mt-3"
              data-testid="deployment-scope-upper-bound-hint"
            >
              {tDeploymentScope("multiTagUpperBoundHint")}
            </p>
          </>
        )}

        {tagsRequired && !hideDraftAlert && tagCount === 0 && (
          <div className="mt-4 p-3 bg-muted border rounded-lg flex items-center gap-2 text-muted-foreground">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">
              No tags selected — this will be saved as a draft. Assign tags to
              deploy to devices.
            </span>
          </div>
        )}

        {footer}
      </GlassCardContent>
    </GlassCard>
  );
};

const SummaryTile = ({
  label,
  value,
  subtitle,
  trailing,
  testId,
}: {
  label: string;
  value: number;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  testId?: string;
}) => (
  <div className="text-center" data-testid={testId}>
    <div className="flex items-center justify-center gap-1.5">
      <div className="text-2xl font-semibold leading-none">{value}</div>
      {trailing}
    </div>
    <div className="text-sm text-muted-foreground mt-1">{label}</div>
    {subtitle}
  </div>
);
